import { create } from "zustand";
import { scopedIdbGet as idbGet, scopedIdbSet as idbSet } from "@/lib/client-scoped-idb";
import { getJson, listDocs, putJsonWithEtag } from "@/lib/docs-api";
import { isWorkshopBffEnabled } from "@/env";
import type { Workshop } from "@/content/workshops";

/**
 * Questionnaire responses store — one submission per user per workshop.
 *
 * Deliberately NOT built on `createWorkshopRecordStore`: the backing
 * `questionnaires` BFF tier is ASYMMETRIC (every role may write; only the
 * facilitation side may read — see services/bff/app/policy.py). The factory
 * assumes symmetric access: its hydrate pulls the whole tier (403 for a
 * write-only submitter) and its writes are etag-conditional (412 on a
 * re-submit from a caller who can't GET the current etag). This store keeps
 * the two lanes separate instead:
 *
 *   - `submit()`  — the participant lane. Upserts the caller's own response
 *     into IDB, then blind-overwrite PUTs it to the tier. Works for every
 *     role; never reads the tier.
 *   - `loadAll()` — the facilitation lane. Prefix-lists the workshop's
 *     responses and fans out GETs. Only called from the results modal, which
 *     is gated to roles that hold tier read — no background polling, so
 *     write-only roles never generate 403 noise. Not in SYNCED_STORES.
 *
 * Blob path: `<wsId>/responses/<userKey(username)>.json`. The path key is a
 * sanitised username (ZITADEL usernames are email-like); the verbatim
 * `username` inside the JSON body is the source of truth. Tier-level authz
 * means any writer could overwrite another's blob — accepted per the ADR 0011
 * need-to-know model; the BFF stamps `uploaded_by` metadata for audit.
 *
 * In IDB mode (`VITE_WORKSHOP_BACKEND=idb`) both lanes stay local — the
 * offline/demo posture, single shared browser.
 */

export type QuestionnaireResponse = {
  workshopId: Workshop["id"];
  /** Verbatim login username — the upsert identity for the submission. */
  username: string;
  displayName: string;
  role: string;
  /** ms epoch of the most recent (re-)submission. */
  submittedAt: number;
  /** Answers keyed by question id — string (text/choice) or number (scale). */
  answers: Record<string, string | number>;
  /**
   * Human-readable Q&A prose for RAG indexing. Built at submit from
   * question labels + answers so Qdrant hits contain the prompt text,
   * not just opaque ids like `q1`. Optional on legacy blobs.
   */
  searchableText?: string;
};

/** Build searchable Q&A prose for the ingestion flattener. */
export function buildQuestionnaireSearchableText(
  questions: { id: string; label: string }[],
  answers: Record<string, string | number>,
): string {
  const lines: string[] = [];
  for (const q of questions) {
    const answer = answers[q.id];
    if (answer === undefined || answer === "") continue;
    const label = q.label.trim() || q.id;
    lines.push(`Q: ${label}`, `A: ${String(answer)}`, "");
  }
  // Orphan answers (question removed from content) still index.
  const known = new Set(questions.map((q) => q.id));
  for (const [qid, answer] of Object.entries(answers)) {
    if (known.has(qid) || answer === undefined || answer === "") continue;
    lines.push(`Q: ${qid}`, `A: ${String(answer)}`, "");
  }
  return lines.join("\n").trim();
}

type EntriesByWorkshop = Record<Workshop["id"], QuestionnaireResponse[]>;

type LoadState = "idle" | "loading" | "loaded" | "error";

type QuestionnaireResponsesState = {
  entriesByWorkshop: EntriesByWorkshop;
  isHydrated: boolean;
  /** Facilitation-lane fetch status (results modal only). */
  loadState: LoadState;
  loadError: string | null;
  hydrate: () => Promise<void>;
  /**
   * Upsert the caller's own response (participant lane). Resolves once the
   * local write has landed; rejects if the BFF mirror write fails — the
   * local copy is kept either way so the answers are never lost.
   */
  submit: (
    workshopId: Workshop["id"],
    response: QuestionnaireResponse,
  ) => Promise<void>;
  /**
   * Pull every response for a workshop from the tier (facilitation lane).
   * Merges by username, remote wins; local-only entries (an in-flight own
   * submission) survive. No-op in IDB mode.
   */
  loadAll: (workshopId: Workshop["id"]) => Promise<void>;
};

const IDB_KEY = "xaes-questionnaire-responses";

const TIER = "questionnaires";

const WORKSHOP_IDS: Workshop["id"][] = [
  "WS1",
  "WS2",
  "WS3",
  "WS4",
  "WS5",
  "WS6",
  "WS7",
  "WS8",
];

const emptyEntries = (): EntriesByWorkshop => ({
  WS1: [],
  WS2: [],
  WS3: [],
  WS4: [],
  WS5: [],
  WS6: [],
  WS7: [],
  WS8: [],
});

/**
 * Path-safe key for a username. ZITADEL usernames are email-like; blob
 * paths want a tame charset. Lowercase, keep `[a-z0-9._-]`, everything
 * else → `-`. Collisions after sanitising merge two users' upsert target —
 * tolerated (the JSON body's verbatim `username` still distinguishes them).
 */
export function userKey(username: string): string {
  const key = username.toLowerCase().replace(/[^a-z0-9._-]+/g, "-");
  return key || "anon";
}

export function responsePath(
  workshopId: Workshop["id"],
  username: string,
): string {
  return `${workshopId}/responses/${userKey(username)}.json`;
}

/** Best-effort sanitiser — same threat model as the sibling stores:
 *  anything unrecognisable in the persisted map is dropped. */
function sanitise(raw: unknown): EntriesByWorkshop {
  const out = emptyEntries();
  if (typeof raw !== "object" || raw === null) return out;
  const obj = raw as Record<string, unknown>;
  for (const id of WORKSHOP_IDS) {
    const list = obj[id];
    if (!Array.isArray(list)) continue;
    out[id] = list
      .map((item) => sanitiseResponse(item, id))
      .filter((r): r is QuestionnaireResponse => r !== null);
  }
  return out;
}

function sanitiseResponse(
  raw: unknown,
  fallbackWorkshopId: Workshop["id"],
): QuestionnaireResponse | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.username !== "string" || r.username.length === 0) return null;
  const answersRaw =
    typeof r.answers === "object" && r.answers !== null
      ? (r.answers as Record<string, unknown>)
      : {};
  const answers: Record<string, string | number> = {};
  for (const [qid, value] of Object.entries(answersRaw)) {
    if (typeof value === "string" || typeof value === "number") {
      answers[qid] = value;
    }
  }
  return {
    workshopId:
      typeof r.workshopId === "string" &&
      (WORKSHOP_IDS as string[]).includes(r.workshopId)
        ? (r.workshopId as Workshop["id"])
        : fallbackWorkshopId,
    username: r.username,
    displayName: typeof r.displayName === "string" ? r.displayName : r.username,
    role: typeof r.role === "string" ? r.role : "",
    submittedAt: typeof r.submittedAt === "number" ? r.submittedAt : 0,
    answers,
    searchableText:
      typeof r.searchableText === "string" && r.searchableText.trim()
        ? r.searchableText
        : undefined,
  };
}

async function persist(entries: EntriesByWorkshop): Promise<void> {
  try {
    await idbSet(IDB_KEY, entries);
  } catch {
    /* best-effort durability — matches the sibling stores */
  }
}

/** Union two buckets by username; `remote` wins on collision. */
function mergeByUsername(
  remote: QuestionnaireResponse[],
  local: QuestionnaireResponse[],
): QuestionnaireResponse[] {
  const seen = new Set(remote.map((r) => r.username));
  return [...remote, ...local.filter((r) => !seen.has(r.username))];
}

export const useQuestionnaireResponses = create<QuestionnaireResponsesState>(
  (set, get) => ({
    entriesByWorkshop: emptyEntries(),
    isHydrated: false,
    loadState: "idle",
    loadError: null,

    hydrate: async () => {
      if (get().isHydrated) return;
      try {
        const raw = await idbGet(IDB_KEY);
        set({ entriesByWorkshop: sanitise(raw), isHydrated: true });
      } catch {
        set({ isHydrated: true });
      }
    },

    submit: async (workshopId, response) => {
      const current = get().entriesByWorkshop;
      const bucket = current[workshopId] ?? [];
      const next: EntriesByWorkshop = {
        ...current,
        [workshopId]: [
          ...bucket.filter((r) => r.username !== response.username),
          response,
        ],
      };
      set({ entriesByWorkshop: next });
      void persist(next);

      if (!isWorkshopBffEnabled()) return;
      // Blind overwrite by design: the path is per-user, so the only writer
      // this can race is the same user in another tab — last-write-wins is
      // the wanted semantics, and a write-only role can't GET an etag anyway.
      await putJsonWithEtag(TIER, responsePath(workshopId, response.username), {
        ...response,
        workshopId,
      });
    },

    loadAll: async (workshopId) => {
      if (!isWorkshopBffEnabled()) {
        set({ loadState: "loaded", loadError: null });
        return;
      }
      set({ loadState: "loading", loadError: null });
      try {
        const blobs = await listDocs(TIER, `${workshopId}/responses/`);
        const fetched = await Promise.all(
          blobs.map((b) => getJson<Record<string, unknown>>(TIER, b.path)),
        );
        const remote = fetched
          .map((raw) => (raw ? sanitiseResponse(raw, workshopId) : null))
          .filter((r): r is QuestionnaireResponse => r !== null);

        const current = get().entriesByWorkshop;
        const next: EntriesByWorkshop = {
          ...current,
          [workshopId]: mergeByUsername(remote, current[workshopId] ?? []),
        };
        set({ entriesByWorkshop: next, loadState: "loaded", loadError: null });
        void persist(next);
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Couldn't load responses";
        set({ loadState: "error", loadError: message });
      }
    },
  }),
);
